import { t } from './i18n.js';
import { useEffect, useMemo, useState } from 'react';
import { ListChecks, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, send } from './api.js';
import { IconButton, Loading, Modal, Notice } from './components.js';

interface Member {
  mailboxId: string;
  address: string;
  name: string;
  kind: string;
}
interface MailList {
  id: string;
  address: string;
  name: string;
  description: string;
  sendPolicy: 'members' | 'organization' | 'anyone';
  members: Member[];
}
interface Form {
  id?: string;
  address?: string;
  name: string;
  local: string;
  domainId: string;
  description: string;
  sendPolicy: MailList['sendPolicy'];
  memberIds: string[];
}
export const policyLabels: Record<MailList['sendPolicy'], string> = {
  members: 'Les membres de la liste',
  organization: 'Toute votre organisation',
  anyone: 'Tout expéditeur, y compris externe',
};
export function MailLists({ notify }: { notify: (s: string) => void }) {
  const [lists, setLists] = useState<MailList[]>(),
    [users, setUsers] = useState<any[]>([]),
    [shared, setShared] = useState<any[]>([]),
    [domains, setDomains] = useState<any[]>([]),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [form, setForm] = useState<Form>(),
    [confirm, setConfirm] = useState<MailList>();
  const load = async () => {
    const [l, u, s, d] = await Promise.all([
      api<MailList[]>('/admin/lists'),
      api<any[]>('/admin/users'),
      api<any[]>('/admin/shared-mailboxes'),
      api<any[]>('/admin/domains'),
    ]);
    setLists(l);
    setUsers(u);
    setShared(s);
    setDomains(d);
  };
  useEffect(() => {
    void load().catch((e) => setError(e.message));
  }, []);
  const candidates = useMemo(() => {
    const map = new Map<string, Member>();
    for (const u of users)
      if (u.mailboxId && u.status === 'active')
        map.set(u.mailboxId, {
          mailboxId: u.mailboxId,
          address: u.address,
          name: u.name,
          kind: 'personal',
        });
    for (const b of shared)
      if (b.kind === 'shared' && b.status === 'active' && !map.has(b.id))
        map.set(b.id, {
          mailboxId: b.id,
          address: b.address,
          name: b.name ?? b.address,
          kind: 'shared',
        });
    return [...map.values()].sort((a, b) => a.address.localeCompare(b.address));
  }, [users, shared]);
  const startCreate = () =>
    setForm({
      name: '',
      local: '',
      domainId: domains.find((d) => d.ownership === 'verified')?.id ?? domains[0]?.id ?? '',
      description: '',
      sendPolicy: 'organization',
      memberIds: [],
    });
  const startEdit = (l: MailList) =>
    setForm({
      id: l.id,
      address: l.address,
      name: l.name,
      local: '',
      domainId: '',
      description: l.description,
      sendPolicy: l.sendPolicy,
      memberIds: l.members.map((m) => m.mailboxId),
    });
  const save = async () => {
    if (!form) return;
    setBusy(true);
    setError('');
    try {
      const body = {
        name: form.name,
        description: form.description,
        sendPolicy: form.sendPolicy,
        memberIds: form.memberIds,
      };
      if (form.id) await send(`/admin/lists/${form.id}`, body, 'PATCH');
      else await send('/admin/lists', { ...body, localPart: form.local, domainId: form.domainId });
      notify(form.id ? t('Liste mise à jour.') : t('Liste créée.'));
      setForm(undefined);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (l: MailList) => {
    setBusy(true);
    try {
      await api(`/admin/lists/${l.id}`, { method: 'DELETE' });
      setConfirm(undefined);
      notify(t('Liste supprimée.'));
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const toggleMember = (id: string, on: boolean) =>
    setForm(
      (f) =>
        f && {
          ...f,
          memberIds: on ? [...new Set([...f.memberIds, id])] : f.memberIds.filter((x) => x !== id),
        },
    );
  return (
    <div className="mail-lists">
      <div className="rules-head">
        <p className="rules-intro">
          {t(
            'Une liste interne distribue chaque message à ses membres, boîtes nominatives ou partagées de votre organisation, sans unité ni quota supplémentaire. Cent membres au maximum.',
          )}
        </p>
        <button className="primary" onClick={startCreate} disabled={!domains.length}>
          <Plus size={16} /> {t('Nouvelle liste')}
        </button>
      </div>
      {error && !form && <Notice type="error">{error}</Notice>}
      {!lists ? (
        <Loading label={t('Chargement des listes…')} />
      ) : lists.length === 0 ? (
        <div className="empty-state">
          <ListChecks />
          <h3>{t('Aucune liste interne')}</h3>
          <p>
            {t('Créez par exemple equipe@votre-domaine pour écrire à tous vos collaborateurs.')}
          </p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('Adresse')}</th>
                <th>{t('Nom')}</th>
                <th>{t('Membres')}</th>
                <th>{t('Qui peut écrire')}</th>
                <th>{t('Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {lists.map((l) => (
                <tr key={l.id}>
                  <td>
                    <span className="inline-icon">
                      <ListChecks size={16} />
                      {l.address}
                    </span>
                  </td>
                  <td>{l.name}</td>
                  <td title={l.members.map((m) => m.address).join(', ')}>
                    {l.members.length} membre{l.members.length > 1 ? 's' : ''}
                  </td>
                  <td>{t(policyLabels[l.sendPolicy])}</td>
                  <td>
                    <IconButton
                      label={`Modifier la liste ${l.address}`}
                      onClick={() => startEdit(l)}
                    >
                      <Pencil size={17} />
                    </IconButton>
                    <IconButton
                      label={`Supprimer la liste ${l.address}`}
                      onClick={() => setConfirm(l)}
                      disabled={busy}
                    >
                      <Trash2 size={17} />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {form && (
        <Modal
          title={form.id ? `Modifier ${form.address}` : 'Nouvelle liste interne'}
          onClose={() => setForm(undefined)}
          wide
        >
          <form
            className="stack-form"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <label>
              Nom de la liste
              <input
                required
                maxLength={120}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('Équipe commerciale')}
              />
            </label>
            {!form.id && (
              <label>
                Adresse de la liste
                <div className="split-field">
                  <input
                    aria-label={t('Partie locale')}
                    required
                    maxLength={64}
                    value={form.local}
                    onChange={(e) => setForm({ ...form, local: e.target.value })}
                    placeholder="equipe"
                  />
                  <span>@</span>
                  <select
                    aria-label={t('Domaine')}
                    value={form.domainId}
                    onChange={(e) => setForm({ ...form, domainId: e.target.value })}
                    required
                  >
                    {domains.map((d) => (
                      <option value={d.id} key={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
            )}
            <label>
              {t('Description')} <small>{t('(facultative)')}</small>
              <input
                maxLength={500}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
            <label>
              {t('Qui peut écrire à la liste')}
              <select
                value={form.sendPolicy}
                onChange={(e) =>
                  setForm({ ...form, sendPolicy: e.target.value as MailList['sendPolicy'] })
                }
              >
                {(Object.keys(policyLabels) as MailList['sendPolicy'][]).map((p) => (
                  <option key={p} value={p}>
                    {t(policyLabels[p])}
                  </option>
                ))}
              </select>
            </label>
            <fieldset className="list-members">
              <legend>{t('Membres ({n}/100)', { n: form.memberIds.length })}</legend>
              {candidates.length === 0 && (
                <p className="muted">{t('Aucune boîte active disponible.')}</p>
              )}
              {candidates.map((c) => (
                <label key={c.mailboxId} className="check-row">
                  <input
                    type="checkbox"
                    checked={form.memberIds.includes(c.mailboxId)}
                    disabled={!form.memberIds.includes(c.mailboxId) && form.memberIds.length >= 100}
                    onChange={(e) => toggleMember(c.mailboxId, e.target.checked)}
                  />
                  <span>
                    {c.address}
                    <small>
                      {' '}
                      · {c.name}
                      {c.kind === 'shared' ? ' · ' + t('boîte partagée') : ''}
                    </small>
                  </span>
                </label>
              ))}
            </fieldset>
            {error && <Notice type="error">{error}</Notice>}
            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setForm(undefined)}>
                {t('Annuler')}
              </button>
              <button className="primary" disabled={busy}>
                {busy ? t('Enregistrement…') : form.id ? t('Enregistrer') : t('Créer la liste')}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {confirm && (
        <Modal title={t('Supprimer cette liste')} onClose={() => setConfirm(undefined)}>
          <p className="modal-body">
            {t(
              'Les futurs messages envoyés à {address} seront refusés. Les messages déjà distribués restent dans les boîtes des membres.',
              { address: confirm.address },
            )}
          </p>
          <div className="modal-actions">
            <button className="text-button" onClick={() => setConfirm(undefined)}>
              {t('Annuler')}
            </button>
            <button className="danger" onClick={() => void remove(confirm)} disabled={busy}>
              {t('Supprimer')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
